import { useMemo } from 'react';
import { setPathname } from './storeHelpers';
import { runDeferredSiteModelLoadIfNeeded } from './deferredSiteModelLoad';
import { ACCESS_MULTIPLIERS, siteCalculationState } from '../services/siteCalculationState';
import { useViewerStore } from '../store/store';

export const SITE_VIEWER_UNAVAILABLE_TITLE = '3D viewer unavailable';

const DEFAULT_SITE_VIEWER_MESSAGE =
    'No Aurora/EagleView design data was found for this site. Try reloading the site, or verify in Salesforce that a design has been loaded before continuing.';

/** User-facing message when Aurora/EagleView site data is missing. */
export function siteViewerUnavailableMessage(fallback?: string | null): string {
    const text = fallback?.trim();
    return text || DEFAULT_SITE_VIEWER_MESSAGE;
}

export function getSiteViewerBlockedMessage(): string | null {
    const { siteLoadingError, siteDesigns, loadingCompleted } = useViewerStore.getState();
    if (siteLoadingError?.trim()) {
        return siteLoadingError.trim();
    }
    if (loadingCompleted && !siteDesigns?.aurora && !siteDesigns?.eagleView) {
        return DEFAULT_SITE_VIEWER_MESSAGE;
    }
    return null;
}

export function isSiteViewer3dBlocked(): boolean {
    return getSiteViewerBlockedMessage() != null;
}

export function showSiteViewerUnavailableModal(message?: string | null): void {
    useViewerStore.setState({
        siteViewerUnavailablePrompt: siteViewerUnavailableMessage(message ?? getSiteViewerBlockedMessage()),
    });
}

export function dismissSiteViewerUnavailableModal(): void {
    useViewerStore.setState({ siteViewerUnavailablePrompt: null });
}

/**
 * Opens the 3D viewer only when site model data loaded successfully.
 * @returns true when navigation ran; false when blocked (modal shown).
 */
export function tryOpenSiteViewer3d(onOpen: () => void): boolean {
    const blockedMessage = getSiteViewerBlockedMessage();
    if (blockedMessage) {
        showSiteViewerUnavailableModal(blockedMessage);
        return false;
    }
    onOpen();
    return true;
}

/** Defer shade/production pipeline while the oneBUTTON dashboard is the active surface. */
export function deferSiteViewerCalculationsOnLoad(): void {
    if (!useViewerStore.getState().isDashboardOpen) return;
    useViewerStore.setState({ siteViewerCalculationsPending: true });
}

export function startSiteViewerCalculationsFromLoad(): void {
    if (useViewerStore.getState().isDashboardOpen) {
        deferSiteViewerCalculationsOnLoad();
        return;
    }
    siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS, 0);
    void siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
}

/** Run the site calculation chain deferred during dashboard-first load. */
export function runPendingSiteViewerCalculations(): void {
    const { siteViewerCalculationsPending } = useViewerStore.getState();
    if (!siteViewerCalculationsPending) return;
    useViewerStore.setState({ siteViewerCalculationsPending: false });
    siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS, 0);
    void siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
}

/** Close dashboard and open the roof / 3D configure view (original oneDRAW entry). */
export function openSiteViewer3dFromDashboard(
    setIsDashboardOpen: (next: boolean) => void,
): boolean {
    return tryOpenSiteViewer3d(() => {
        setPathname('/configure/roof');
        setIsDashboardOpen(false);
        void runDeferredSiteModelLoadIfNeeded()
            .catch(() => {
                /* errors logged in deferred loader */
            })
            .finally(() => {
                runPendingSiteViewerCalculations();
            });
    });
}

export function useSiteViewer3dBlocked(): { blocked: boolean; message: string | null } {
    const siteLoadingError = useViewerStore((s) => s.siteLoadingError);
    const siteDesigns = useViewerStore((s) => s.siteDesigns);
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);

    return useMemo(() => {
        if (siteLoadingError?.trim()) {
            return { blocked: true, message: siteLoadingError.trim() };
        }
        if (loadingCompleted && !siteDesigns?.aurora && !siteDesigns?.eagleView) {
            return { blocked: true, message: DEFAULT_SITE_VIEWER_MESSAGE };
        }
        return { blocked: false, message: null };
    }, [siteLoadingError, siteDesigns?.aurora, siteDesigns?.eagleView, loadingCompleted]);
}
